import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import PublicTopNav from '../components/PublicTopNav';
import SiteFooter from '../components/SiteFooter';
import { CONTACT_PATH, LEGAL_PATHS, OFFERING_PATH } from '../legal/company';
import usePageMeta from '../hooks/usePageMeta';

type Language = 'en' | 'hi';

interface Offering {
  title: string;
  detail: string;
}

interface Copy {
  languageLabel: string;
  title: string;
  intro: string;
  includedHeading: string;
  includedNote: string;
  excludedHeading: string;
  excludedNote: string;
  included: Offering[];
  excluded: Offering[];
  priceNote: string;
  ctaHeading: string;
  ctaBody: string;
  ctaButton: string;
  contactLink: string;
  termsPrefix: string;
  termsLink: string;
  termsSuffix: string;
}

/**
 * Kept as one object per language rather than an i18n library: this is the only
 * bilingual page in the app, and both versions have to be read side by side when
 * the offering changes, which a flat key file makes harder.
 */
const COPY: Record<Language, Copy> = {
  en: {
    languageLabel: 'English',
    title: 'What you get, and what you do not',
    intro:
      'Everything below is part of a website plan. We have also listed what is not included, so there are no surprises later.',
    includedHeading: 'Included in your plan',
    includedNote: 'Available on both the monthly and yearly plan.',
    excludedHeading: 'Not included',
    excludedNote: 'Some of these can be arranged as a Custom quote — just ask.',
    included: [
      {
        title: 'A website built for you by AI',
        detail:
          'Describe your business by typing or speaking in English or Hindi, or upload photos of your shop and work. You get a complete website with design and written content.',
      },
      {
        title: 'All the sections a business needs',
        detail:
          'Hero, products or a categorised catalogue, services, business hours, offers, gallery, team, customer reviews, FAQs, pricing, blog, video, newsletter signup, social links and a contact form. Add, remove or reorder them whenever you like.',
      },
      {
        title: 'A basic logo, colours and layouts',
        detail:
          'AI creates a basic logo for your business while the website is being generated. Colours, fonts and section layouts you can change yourself any time, or apply a ready-made theme.',
      },
      {
        title: 'Unlimited edits with AI',
        detail:
          'Rewrite any section, add a new one, or restyle the whole site from a short instruction typed or spoken. Publish again when it looks right.',
      },
      {
        title: 'Free OnTheWeb address, hosting and HTTPS',
        detail:
          'Every published website gets its own live link on ontheweb.ai. Hosting and the security certificate are on us.',
      },
      {
        title: 'Connect your own domain',
        detail:
          'Point a domain you own at your website. We show the exact DNS record, verify it, and set up HTTPS automatically.',
      },
      {
        title: 'Search engine settings',
        detail:
          'Control your page title, description and keywords, upload your own favicon, and get a sitemap so Google and other search engines can find the site.',
      },
      {
        title: 'Products in bulk',
        detail:
          'Export your catalogue to a spreadsheet, edit it in Excel or Google Sheets, and import the same file back to add or update many products at once.',
      },
      {
        title: 'Enquiries, newsletter and visit analytics',
        detail:
          'Contact form messages and newsletter signups arrive in your dashboard, along with how many people visited your site.',
      },
      {
        title: 'Looks right on every phone',
        detail: 'Built mobile first, then tablet and desktop, so it is ready to share the moment you publish.',
      },
      {
        title: 'Support and GST invoices',
        detail: 'Reach us on WhatsApp, email or phone. Your invoices, with GST, stay in your dashboard.',
      },
    ],
    excluded: [
      {
        title: 'A separate page for every section',
        detail:
          'Your website is one page carrying all your business information — about, services, hours, offers, gallery, team, reviews and contact sit on it as sections a visitor scrolls through. Only your full product catalogue and each blog article open as pages of their own.',
      },
      {
        title: 'The cost of a domain name',
        detail:
          'Buying and renewing a domain is paid to your domain registrar. We connect it for free, but the domain charge is separate.',
      },
      {
        title: 'Online payments and a shopping cart',
        detail:
          'Visitors can see your prices and send an enquiry, but the website does not take orders or collect payments.',
      },
      {
        title: 'Stock and inventory tracking',
        detail: 'Products do not keep a stock count, and the site will not mark items as out of stock.',
      },
      {
        title: 'Business email on your domain',
        detail: 'A mailbox such as you@yourdomain.com is not part of a website plan.',
      },
      {
        title: 'ERP, CRM or other custom software',
        detail: 'These are available as a Custom quote alongside your website, not within the standard plans.',
      },
      {
        title: 'A promised Google ranking, or running ads',
        detail:
          'We set up the search engine basics properly, but no one can promise a first-page position, and managing paid ads is not included.',
      },
      {
        title: 'A premium logo, or a logo change later',
        detail:
          'The logo you get is a basic one. A premium, designer-made logo is not part of the plan, and the logo cannot be changed after your website has been generated.',
      },
      {
        title: 'Photoshoots and professional copywriting',
        detail:
          'Content is generated from what you provide and you can edit it freely. A photographer or human writer is not part of the plan.',
      },
      {
        title: 'A mobile app',
        detail: 'Your website works in any phone browser, but a Play Store or App Store app is not included.',
      },
      {
        title: 'Custom code and third-party integrations',
        detail: 'Custom scripts or connections to other software are handled as a Custom quote.',
      },
      {
        title: 'A Hindi version of your website',
        detail:
          'You can speak Hindi while creating or editing and we translate it, but the website itself is generated in English. You can type Hindi text in while editing; there is no language switcher for your visitors.',
      },
      {
        title: 'Moving an existing website across',
        detail: 'We build a new website for you. The design or data from a site you already have is not migrated.',
      },
    ],
    priceNote: 'Monthly ₹499 or yearly ₹1,299, plus 18% GST. Domain charges are separate.',
    ctaHeading: 'Ready to see your own website?',
    ctaBody: 'Describe your business and look at the result before you pay anything.',
    ctaButton: 'Create your website',
    contactLink: 'Ask us a question',
    termsPrefix: 'This page explains our offering in plain language. The',
    termsLink: 'Terms & Conditions',
    termsSuffix: 'are what apply to your plan.',
  },
  hi: {
    languageLabel: 'हिंदी',
    title: 'क्या मिलेगा, और क्या नहीं',
    intro:
      'नीचे दी गई सभी चीज़ें वेबसाइट प्लान में शामिल हैं। साथ में यह भी लिखा है कि क्या शामिल नहीं है, ताकि बाद में कोई उलझन न हो।',
    includedHeading: 'आपके प्लान में शामिल',
    includedNote: 'महीने और साल, दोनों प्लान में उपलब्ध।',
    excludedHeading: 'इनमें शामिल नहीं',
    excludedNote: 'इनमें से कुछ चीज़ें कस्टम कोटेशन पर हो सकती हैं — बस हमें बताइए।',
    included: [
      {
        title: 'AI से बनी आपकी वेबसाइट',
        detail:
          'अपने बिज़नेस के बारे में अंग्रेज़ी या हिंदी में लिखकर या बोलकर बताइए, या दुकान और काम की फ़ोटो अपलोड कीजिए। डिज़ाइन और लिखे हुए कंटेंट के साथ पूरी वेबसाइट तैयार मिलेगी।',
      },
      {
        title: 'बिज़नेस के लिए ज़रूरी सभी सेक्शन',
        detail:
          'हीरो, प्रोडक्ट या कैटेगरी वाला कैटलॉग, सर्विसेज़, खुलने का समय, ऑफ़र, गैलरी, टीम, ग्राहकों की राय, सवाल-जवाब, प्राइसिंग, ब्लॉग, वीडियो, न्यूज़लेटर साइनअप, सोशल लिंक और कॉन्टैक्ट फ़ॉर्म। इन्हें जब चाहें जोड़ें, हटाएँ या क्रम बदलें।',
      },
      {
        title: 'बेसिक लोगो, रंग और लेआउट',
        detail:
          'वेबसाइट बनते समय AI आपके बिज़नेस का एक बेसिक लोगो बना देता है। रंग, फ़ॉन्ट और सेक्शन का लेआउट आप जब चाहें खुद बदल सकते हैं, या तैयार थीम लगा सकते हैं।',
      },
      {
        title: 'AI से जितनी बार चाहें बदलाव',
        detail:
          'किसी भी सेक्शन को दोबारा लिखवाएँ, नया सेक्शन जोड़ें, या पूरी साइट का रंग-रूप बदलें — छोटा-सा निर्देश लिखकर या बोलकर दीजिए। पसंद आने पर दोबारा पब्लिश कर दीजिए।',
      },
      {
        title: 'मुफ़्त OnTheWeb पता, होस्टिंग और HTTPS',
        detail:
          'हर पब्लिश वेबसाइट को ontheweb.ai पर अपना लाइव लिंक मिलता है। होस्टिंग और सुरक्षा सर्टिफ़िकेट हमारी तरफ़ से।',
      },
      {
        title: 'अपना डोमेन जोड़िए',
        detail:
          'अपने डोमेन को वेबसाइट से जोड़िए। कौन-सा DNS रिकॉर्ड लगाना है, यह हम बताते हैं, वेरिफ़ाई करते हैं और HTTPS अपने आप सेट कर देते हैं।',
      },
      {
        title: 'सर्च इंजन की सेटिंग',
        detail:
          'पेज का टाइटल, डिस्क्रिप्शन और कीवर्ड आपके हाथ में। अपना फ़ेविकॉन अपलोड कीजिए, और साइटमैप की मदद से Google जैसे सर्च इंजन आपकी साइट तक पहुँचते हैं।',
      },
      {
        title: 'एक साथ बहुत सारे प्रोडक्ट',
        detail:
          'कैटलॉग को शीट में एक्सपोर्ट कीजिए, Excel या Google Sheets में बदलाव कीजिए, और वही फ़ाइल इम्पोर्ट कर दीजिए — एक बार में कई प्रोडक्ट जुड़ या अपडेट हो जाएँगे।',
      },
      {
        title: 'पूछताछ, न्यूज़लेटर और विज़िट के आंकड़े',
        detail:
          'कॉन्टैक्ट फ़ॉर्म के मैसेज और न्यूज़लेटर साइनअप आपके डैशबोर्ड में आते हैं, साथ में कितने लोगों ने साइट देखी यह भी।',
      },
      {
        title: 'हर फ़ोन पर सही दिखती है',
        detail: 'पहले मोबाइल के हिसाब से बनी, फिर टैबलेट और कंप्यूटर — पब्लिश करते ही शेयर करने लायक।',
      },
      {
        title: 'सपोर्ट और GST इनवॉइस',
        detail: 'WhatsApp, ईमेल या फ़ोन पर हमसे बात कीजिए। GST वाले इनवॉइस आपके डैशबोर्ड में रहते हैं।',
      },
    ],
    excluded: [
      {
        title: 'हर सेक्शन के लिए अलग पेज',
        detail:
          'आपकी वेबसाइट एक ही पेज की होती है, जिस पर बिज़नेस की सारी जानकारी — परिचय, सर्विसेज़, समय, ऑफ़र, गैलरी, टीम, ग्राहकों की राय और कॉन्टैक्ट — सेक्शन के रूप में एक के नीचे एक रहती है। सिर्फ़ पूरा प्रोडक्ट कैटलॉग और हर ब्लॉग लेख अपने अलग पेज पर खुलते हैं।',
      },
      {
        title: 'डोमेन नेम का खर्च',
        detail:
          'डोमेन खरीदने और रिन्यू कराने का पैसा आपके डोमेन रजिस्ट्रार को देना होता है। जोड़ने का काम हम मुफ़्त करते हैं, पर डोमेन का खर्च अलग है।',
      },
      {
        title: 'ऑनलाइन पेमेंट और शॉपिंग कार्ट',
        detail:
          'ग्राहक आपके दाम देख सकते हैं और पूछताछ भेज सकते हैं, लेकिन वेबसाइट पर ऑर्डर लेना या पेमेंट लेना शामिल नहीं है।',
      },
      {
        title: 'स्टॉक और इन्वेंटरी का हिसाब',
        detail: 'प्रोडक्ट में स्टॉक की गिनती नहीं रहती, और साइट पर "स्टॉक ख़त्म" जैसा हिसाब नहीं दिखता।',
      },
      {
        title: 'अपने डोमेन पर बिज़नेस ईमेल',
        detail: 'you@yourdomain.com जैसा मेलबॉक्स वेबसाइट प्लान में शामिल नहीं है।',
      },
      {
        title: 'ERP, CRM या कस्टम सॉफ़्टवेयर',
        detail: 'ये आम प्लान में नहीं, बल्कि वेबसाइट के साथ कस्टम कोटेशन पर उपलब्ध हैं।',
      },
      {
        title: 'Google में रैंक का वादा, या ऐड चलाना',
        detail:
          'सर्च इंजन की ज़रूरी सेटिंग हम ठीक से कर देते हैं, पर पहले पेज पर आने का वादा कोई नहीं कर सकता, और पेड ऐड चलाना शामिल नहीं है।',
      },
      {
        title: 'प्रीमियम लोगो, या बाद में लोगो बदलना',
        detail:
          'जो लोगो मिलता है वह बेसिक होता है। डिज़ाइनर से बना प्रीमियम लोगो प्लान में शामिल नहीं है, और वेबसाइट बन जाने के बाद लोगो बदला नहीं जा सकता।',
      },
      {
        title: 'फ़ोटोशूट और पेशेवर राइटिंग',
        detail:
          'कंटेंट आपकी दी हुई जानकारी से बनता है और आप उसे बदल सकते हैं। फ़ोटोग्राफ़र या राइटर की सेवा प्लान में शामिल नहीं है।',
      },
      {
        title: 'मोबाइल ऐप',
        detail: 'आपकी वेबसाइट किसी भी फ़ोन ब्राउज़र में चलती है, पर Play Store या App Store वाला ऐप शामिल नहीं है।',
      },
      {
        title: 'कस्टम कोड और दूसरी सेवाओं से जोड़ना',
        detail: 'कस्टम स्क्रिप्ट या किसी और सॉफ़्टवेयर से जोड़ने का काम कस्टम कोटेशन पर होता है।',
      },
      {
        title: 'वेबसाइट का हिंदी वर्शन',
        detail:
          'बनाते या एडिट करते समय आप हिंदी में बोल सकते हैं और हम उसका अनुवाद कर देते हैं, लेकिन वेबसाइट अंग्रेज़ी में बनती है। एडिट करते समय हिंदी में लिख सकते हैं; विज़िटर के लिए भाषा बदलने वाला बटन नहीं होता।',
      },
      {
        title: 'पुरानी वेबसाइट को लाना',
        detail: 'हम आपके लिए नई वेबसाइट बनाते हैं। पहले से मौजूद साइट का डिज़ाइन या डेटा ट्रांसफ़र नहीं होता।',
      },
    ],
    priceNote: 'महीने का ₹499 या साल का ₹1,299, साथ में 18% GST। डोमेन का खर्च अलग है।',
    ctaHeading: 'अपनी वेबसाइट देखने के लिए तैयार हैं?',
    ctaBody: 'अपने बिज़नेस के बारे में बताइए और पैसे देने से पहले नतीजा देख लीजिए।',
    ctaButton: 'अपनी वेबसाइट बनाइए',
    contactLink: 'कोई सवाल पूछिए',
    termsPrefix: 'यह पेज हमारी सेवा को आसान भाषा में समझाता है। आपके प्लान पर',
    termsLink: 'नियम और शर्तें',
    termsSuffix: 'लागू होती हैं।',
  },
};

const LANGUAGES: Language[] = ['en', 'hi'];

const CheckIcon: React.FC = () => (
  <svg className="w-5 h-5 text-nova-green flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
  </svg>
);

const CrossIcon: React.FC = () => (
  <svg className="w-5 h-5 text-nova-red flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
  </svg>
);

const OfferingList: React.FC<{ items: Offering[]; tone: 'included' | 'excluded' }> = ({ items, tone }) => (
  <ul className="space-y-4">
    {items.map((item) => (
      <li key={item.title} className="flex gap-3">
        {tone === 'included' ? <CheckIcon /> : <CrossIcon />}
        <div>
          <p className="font-semibold text-nova-text">{item.title}</p>
          <p className="text-sm text-nova-text2 mt-1 leading-relaxed">{item.detail}</p>
        </div>
      </li>
    ))}
  </ul>
);

const OfferingPage: React.FC = () => {
  const [language, setLanguage] = useState<Language>('en');
  const copy = COPY[language];

  // Indexed in English at one URL: the Hindi version is a toggle on this same
  // page rather than a separate address, so there is no hreflang pair to declare.
  usePageMeta({
    title: 'No Code Website Builder: What You Get | OnTheWeb.ai',
    description:
      'Know what you’re paying for. Explore OnTheWeb’s no code website builder, from product catalogues to hosting, and see what’s included before choosing a plan.',
    path: OFFERING_PATH,
  });

  return (
    <div className="nova-page">
      <PublicTopNav activePath={OFFERING_PATH} />

      <main className="pt-[68px]">
        {/* lang tells the browser to pick a Devanagari face for the Hindi copy,
            since the app's display fonts only cover Latin. */}
        <div className="max-w-4xl mx-auto px-6 lg:px-12 py-16 sm:py-20" lang={language}>
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-8">
            <div>
              <h1 className="font-display text-3xl sm:text-4xl font-extrabold text-nova-text">{copy.title}</h1>
              <p className="text-nova-text2 mt-3 leading-relaxed">{copy.intro}</p>
            </div>
            <div
              className="flex items-center gap-1 p-1 bg-nova-bg3 border border-nova-border rounded-lg self-start flex-shrink-0"
              role="group"
              aria-label="Language"
            >
              {LANGUAGES.map((option) => (
                <button
                  key={option}
                  type="button"
                  onClick={() => setLanguage(option)}
                  aria-pressed={language === option}
                  lang={option}
                  className={`px-3 py-1.5 rounded-md text-sm font-medium transition-colors ${
                    language === option
                      ? 'bg-nova-sky text-nova-sky4'
                      : 'text-nova-text2 hover:text-nova-text'
                  }`}
                >
                  {COPY[option].languageLabel}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <section className="nova-card p-6">
              <h2 className="font-display text-xl font-bold text-nova-text">{copy.includedHeading}</h2>
              <p className="text-sm text-nova-text3 mt-1 mb-6">{copy.includedNote}</p>
              <OfferingList items={copy.included} tone="included" />
            </section>

            <section className="nova-card p-6">
              <h2 className="font-display text-xl font-bold text-nova-text">{copy.excludedHeading}</h2>
              <p className="text-sm text-nova-text3 mt-1 mb-6">{copy.excludedNote}</p>
              <OfferingList items={copy.excluded} tone="excluded" />
            </section>
          </div>

          <p className="text-sm text-nova-text2 mt-8">{copy.priceNote}</p>

          <div className="nova-card p-6 sm:p-8 mt-8 text-center">
            <h2 className="font-display text-2xl font-bold text-nova-text">{copy.ctaHeading}</h2>
            <p className="text-nova-text2 mt-2">{copy.ctaBody}</p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-3 mt-6">
              <Link to="/login" className="btn-primary px-6 py-3">
                {copy.ctaButton}
              </Link>
              <Link to={CONTACT_PATH} className="btn-ghost px-6 py-3">
                {copy.contactLink}
              </Link>
            </div>
          </div>

          <p className="text-xs text-nova-text3 mt-8">
            {copy.termsPrefix}{' '}
            <Link to={LEGAL_PATHS.terms} className="text-nova-sky hover:text-nova-sky2">
              {copy.termsLink}
            </Link>{' '}
            {copy.termsSuffix}
          </p>
        </div>
      </main>

      <SiteFooter />
    </div>
  );
};

export default OfferingPage;
